import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    consentShopReferralTicket,
    createPartnerRepairRequest,
    listPartnerShops,
    searchNearbyShops,
    validateShopSearchLocation,
} from '../utils/partnerShopApi';
import useCurrentLocation from '../hooks/useCurrentLocation';
import InfoTooltip from './InfoTooltip';
import { normalizeZipCode, ZIP_INVALID_MESSAGE } from '../utils/zipCode';

const DEFAULT_ZIP = '80212';
const LAST_ZIP_STORAGE_KEY = 'carfido.shopDrawer.lastZip';

// Where the drawer opens: the ZIP this driver last searched, else whatever the caller
// passed, else the seed ZIP. Reading storage can throw outright in a locked-down browser,
// so a failure here has to fall through to a usable default rather than break the drawer.
function readLastZip() {
    try {
        const stored = window.localStorage?.getItem(LAST_ZIP_STORAGE_KEY);
        return /^\d{5}$/.test(String(stored || '')) ? stored : '';
    } catch {
        return '';
    }
}

function rememberLastZip(zip) {
    try {
        window.localStorage?.setItem(LAST_ZIP_STORAGE_KEY, zip);
    } catch {
        /* A driver with site data blocked still gets a working search, just no memory of it. */
    }
}

function Icon({ children, className = '' }) {
    return (
        <svg className={className} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            {children}
        </svg>
    );
}

function getShopId(shop) {
    return shop?.partner_shop_id || shop?.id;
}

function normalizeSentIds(sentShopIds) {
    if (!sentShopIds) return new Set();
    if (sentShopIds instanceof Set) return new Set([...sentShopIds].map(String));
    if (Array.isArray(sentShopIds)) return new Set(sentShopIds.map(String));
    return new Set();
}

function isValidZip(value) {
    return Boolean(normalizeZipCode(value));
}

function getPhoneHref(phone) {
    return `tel:${String(phone || '').replace(/[^\d+]/g, '')}`;
}

function Rating({ rating, reviewCount }) {
    if (!rating) return null;
    return (
        <div className="flex items-center gap-1 text-[12px] font-bold text-[#d58300]">
            <Icon className="h-3.5 w-3.5 fill-[#f6b343] text-[#f6b343]"><polygon points="12 2 15 8.5 22 9.2 16.8 13.8 18.3 21 12 17.4 5.7 21 7.2 13.8 2 9.2 9 8.5 12 2" /></Icon>
            <span>{rating}</span>
            {reviewCount ? <span className="font-semibold text-[#414141]">({reviewCount})</span> : null}
        </div>
    );
}

function MobileMechanicBadge() {
    return (
        <span className="inline-flex items-center gap-1 rounded-full border border-[#c9d7f5] bg-[#eef4ff] px-1.5 py-0.5 text-[10px] font-black text-[#2856a3]">
            <Icon className="h-3 w-3"><path d="M3 17h2l2-5h10l2 5h2" /><path d="M5 17v3M19 17v3M8 12l2-4h4l2 4" /><circle cx="8" cy="17" r="1" /><circle cx="16" cy="17" r="1" /></Icon>
            Mobile mechanic
        </span>
    );
}

// #697: one line saying how well the shop fits the diagnosed repair, and why. Only shown
// when the server found evidence; the wording comes from that evidence, never invented.
const MATCH_TONE = {
    confirmed: 'text-[#087a43]',
    likely: 'text-[#414141]',
    unverified: 'text-[#414141]',
};

function MatchNote({ match }) {
    if (!match?.reason) return null;
    return (
        <p data-testid="shop-match" className="mt-2 text-[11px] font-semibold leading-relaxed text-[#414141]">
            <span className={`font-black ${MATCH_TONE[match.tier] || MATCH_TONE.unverified}`}>{match.label}</span>
            {' · '}
            {match.reason}
            {match.vehicleNote ? ` ${match.vehicleNote}` : ''}
        </p>
    );
}

function PartnerShopCard({ shop, sent, requesting, requestDisabledReason = '', onRequest, showAdminLink = false, isMaintenance = false }) {
    const disabled = sent || requesting || Boolean(requestDisabledReason);
    const mapsUrl = `https://maps.google.com/?q=${encodeURIComponent(shop.address || shop.name)}`;
    return (
        <div className="rounded-2xl border border-[#d9d9d9] bg-white p-3.5 shadow-sm">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-[15px] font-black leading-tight text-[#414141]">{shop.name}</h3>
                        <span className="inline-flex items-center gap-1 rounded-full border border-[#bde7d5] bg-[#ecfff5] px-1.5 py-0.5 text-[10px] font-black text-[#087a43]">
                            <Icon className="h-3 w-3"><path d="M20 6 9 17l-5-5" /></Icon>
                            CarFido Partner
                        </span>
                        {shop.is_mobile_mechanic ? <MobileMechanicBadge /> : null}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                        <Rating rating={shop.rating} reviewCount={shop.review_count} />
                    </div>
                </div>
                <div className="flex items-center gap-1.5">
                    {shop.distance_label ? (
                        <span className="rounded-full bg-[#f7f7f7] px-2 py-1 text-[10px] font-black text-[#414141]">
                            {shop.distance_label}
                        </span>
                    ) : null}
                    <div
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-white"
                        style={{ backgroundColor: shop.logo_color || '#414141' }}
                    >
                        <Icon className="h-4 w-4"><path d="M3 9 5 3h14l2 6" /><path d="M4 9h16v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z" /><path d="M9 22V12h6v10" /></Icon>
                    </div>
                </div>
            </div>

            <div className="mt-2 flex flex-col gap-1">
                <p className="flex items-start gap-1.5 text-[12px] font-semibold leading-relaxed text-[#414141]">
                    <Icon className="mt-0.5 h-3 w-3 shrink-0 text-[#a4a4a4]"><path d="M21 10c0 7-9 12-9 12S3 17 3 10a9 9 0 1 1 18 0z" /><circle cx="12" cy="10" r="3" /></Icon>
                    {shop.address}
                </p>
                <div className="flex flex-wrap gap-3 text-[11px] font-black text-[#414141]">
                    {shop.phone ? (
                        <a href={getPhoneHref(shop.phone)} className="inline-flex items-center gap-1">
                            <Icon className="h-3 w-3"><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.3 1.7.6 2.5a2 2 0 0 1-.5 2.1L8 9.5a16 16 0 0 0 6.5 6.5l1.2-1.2a2 2 0 0 1 2.1-.5c.8.3 1.6.5 2.5.6a2 2 0 0 1 1.7 2z" /></Icon>
                            {shop.phone}
                        </a>
                    ) : null}
                    {shop.website ? (
                        <a href={shop.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1">
                            <Icon className="h-3 w-3"><circle cx="12" cy="12" r="10" /><path d="M2 12h20" /><path d="M12 2a15.3 15.3 0 0 1 0 20" /><path d="M12 2a15.3 15.3 0 0 0 0 20" /></Icon>
                            Website
                        </a>
                    ) : null}
                </div>
            </div>

            {Array.isArray(shop.tags) && shop.tags.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                    {shop.tags.map((tag) => (
                        <span key={tag} className="rounded-full bg-[#f5f5f5] px-2 py-1 text-[10px] font-bold text-[#414141]">{tag}</span>
                    ))}
                </div>
            )}
            <MatchNote match={shop.match} />

            {shop.why_recommended && (
                <div className="mt-3 rounded-2xl bg-[#f9f9f9] px-3 py-2">
                    <p className="text-[10px] font-black uppercase tracking-[0.08em] text-[#414141]">Why CarFido Recommends</p>
                    <p className="mt-1 text-[12px] font-semibold leading-relaxed text-[#414141]">{shop.why_recommended}</p>
                </div>
            )}

            <div className="mt-3 grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-[#F9F9F9] px-3 py-2">
                    <p className="text-[10px] font-black uppercase tracking-[0.08em] text-[#414141]">Diagnostic Fee</p>
                    <p className="mt-1 text-[14px] font-black text-[#414141]">{shop.diagnostic_fee_label || 'Varies'}</p>
                </div>
                <div className="rounded-xl bg-[#F9F9F9] px-3 py-2">
                    <p className="text-[10px] font-black uppercase tracking-[0.08em] text-[#414141]">Earliest</p>
                    <p className="mt-1 text-[14px] font-black text-[#414141]">{shop.earliest_available_label || 'Call shop'}</p>
                </div>
            </div>

            <button
                type="button"
                onClick={() => onRequest(shop)}
                disabled={disabled}
                title={requestDisabledReason || undefined}
                className={`mt-4 flex h-10 w-full items-center justify-center gap-2 rounded-xl px-4 text-sm font-black transition active:scale-[0.99] ${
                    sent
                        ? 'bg-[#e8fbef] text-[#15803d]'
                        : requestDisabledReason
                        ? 'bg-[#f1f1f1] text-[#414141]'
                        : 'bg-[#414141] text-white hover:bg-[#2A2A2A]'
                } disabled:cursor-not-allowed disabled:opacity-80`}
            >
                {requesting ? (
                    <>
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/60 border-t-white" />
                        Requesting
                    </>
                ) : sent ? (
                    <>
                        <Icon className="h-4 w-4"><path d="M20 6 9 17l-5-5" /></Icon>
                        Estimate Requested
                    </>
                ) : requestDisabledReason ? (
                    <>
                        <Icon className="h-4 w-4"><path d="M9 12h6" /><path d="M12 9v6" /><circle cx="12" cy="12" r="9" /></Icon>
                        {requestDisabledReason}
                    </>
                ) : (
                    <>
                        <Icon className="h-4 w-4"><path d="M9 11h6" /><path d="M9 15h4" /><rect x="5" y="3" width="14" height="18" rx="2" /></Icon>
                        Request Estimate
                    </>
                )}
            </button>
            {sent && showAdminLink && (
                <a
                    href="/shop"
                    className="mt-2 flex h-9 w-full items-center justify-center rounded-xl bg-[#f5f5f5] px-4 text-xs font-black text-[#414141] transition hover:bg-[#ededed]"
                >
                    Open Shop Admin View
                </a>
            )}
            <div className="mt-2 grid grid-cols-2 gap-2">
                {shop.phone ? (
                    <a href={getPhoneHref(shop.phone)} className="flex items-center justify-center gap-1.5 rounded-xl bg-[#f7f7f7] px-3 py-2 text-[11px] font-black text-[#414141]">
                        <Icon className="h-3.5 w-3.5"><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.3 1.7.6 2.5a2 2 0 0 1-.5 2.1L8 9.5a16 16 0 0 0 6.5 6.5l1.2-1.2a2 2 0 0 1 2.1-.5c.8.3 1.6.5 2.5.6a2 2 0 0 1 1.7 2z" /></Icon>
                        Call Shop
                    </a>
                ) : null}
                <a href={mapsUrl} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-1.5 rounded-xl bg-[#f7f7f7] px-3 py-2 text-[11px] font-black text-[#414141]">
                    <Icon className="h-3.5 w-3.5"><path d="M21 10c0 7-9 12-9 12S3 17 3 10a9 9 0 1 1 18 0z" /><circle cx="12" cy="10" r="3" /></Icon>
                    Directions
                </a>
            </div>
        </div>
    );
}

// Google returns weekdayDescriptions starting at Monday, while JS getDay() starts at
// Sunday — hence the shift. Getting this wrong shows every driver the wrong day's hours.
function getTodayHoursLine(weekdayHours) {
    if (!Array.isArray(weekdayHours) || weekdayHours.length < 7) return '';
    return weekdayHours[(new Date().getDay() + 6) % 7] || '';
}

function NearbyShopCard({ shop }) {
    const [shareCopied, setShareCopied] = useState(false);
    const [hoursOpen, setHoursOpen] = useState(false);
    const todayHours = getTodayHoursLine(shop.weekday_hours);
    const mapsUrl = shop.maps_url || `https://maps.google.com/?q=${encodeURIComponent(shop.address || shop.name)}`;
    const distanceLabel = shop.distance_label || 'Nearby';
    function handleShareReport() {
        const text = `CarFido Diagnostic Report for ${shop.name}`;
        if (navigator.share) {
            navigator.share({ title: 'CarFido Diagnostic Report', text }).catch(() => {});
            return;
        }
        navigator.clipboard?.writeText(text).then(() => {
            setShareCopied(true);
            setTimeout(() => setShareCopied(false), 1600);
        }).catch(() => {});
    }

    return (
        <div className="rounded-2xl border border-[#d9d9d9] bg-white p-3.5 shadow-sm">
            <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-start gap-3">
                    {shop.photo_url ? (
                        <img
                            src={shop.photo_url}
                            alt=""
                            loading="lazy"
                            className="h-14 w-14 shrink-0 rounded-xl object-cover"
                        />
                    ) : null}
                    <div className="min-w-0">
                        <h3 className="text-[15px] font-black leading-tight text-[#414141]">{shop.name}</h3>
                        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                            <Rating rating={shop.rating} reviewCount={shop.review_count} />
                            {shop.open_now === true && (
                                <span className="text-[12px] font-black text-[#087a43]">Open now</span>
                            )}
                            {shop.open_now === false && (
                                <span className="text-[12px] font-bold text-[#b42318]">Closed</span>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                    <span className="rounded-full bg-[#f7f7f7] px-2 py-1 text-[10px] font-black text-[#414141]">{distanceLabel}</span>
                </div>
            </div>
            <p className="mt-2 text-[12px] font-semibold leading-relaxed text-[#414141]">{shop.address}</p>
            {(shop.tags || []).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                    {(shop.tags || []).map((tag) => (
                        <span key={tag} className="rounded-full bg-[#f7f7f7] px-2 py-1 text-[10px] font-bold text-[#414141]">{tag}</span>
                    ))}
                </div>
            )}
            <MatchNote match={shop.match} />
            {todayHours && (
                <div className="mt-3 rounded-xl bg-[#F9F9F9] px-3 py-2">
                    <div className="flex items-center justify-between gap-2">
                        <p className="text-[12px] font-bold text-[#414141]">{todayHours}</p>
                        <button
                            type="button"
                            onClick={() => setHoursOpen((open) => !open)}
                            className="shrink-0 text-[11px] font-black text-[#D81F26] underline"
                        >
                            {hoursOpen ? 'Hide hours' : 'All hours'}
                        </button>
                    </div>
                    {hoursOpen && (
                        <ul className="mt-2 flex flex-col gap-0.5">
                            {shop.weekday_hours.map((line) => (
                                <li key={line} className="text-[11px] font-semibold text-[#414141]">{line}</li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
            <div className="mt-4 grid grid-cols-2 gap-2">
                {shop.phone && (
                    <a href={getPhoneHref(shop.phone)} className="flex items-center justify-center gap-1.5 rounded-xl bg-[#f5f5f5] px-3 py-2 text-xs font-black text-[#414141]">
                        <Icon className="h-3.5 w-3.5"><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.3 1.7.6 2.5a2 2 0 0 1-.5 2.1L8 9.5a16 16 0 0 0 6.5 6.5l1.2-1.2a2 2 0 0 1 2.1-.5c.8.3 1.6.5 2.5.6a2 2 0 0 1 1.7 2z" /></Icon>
                        Call
                    </a>
                )}
                <a href={mapsUrl} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-1.5 rounded-xl bg-[#f7f7f7] px-3 py-2 text-xs font-black text-[#414141]">
                    <Icon className="h-3.5 w-3.5"><path d="M21 10c0 7-9 12-9 12S3 17 3 10a9 9 0 1 1 18 0z" /><circle cx="12" cy="10" r="3" /></Icon>
                    Map
                </a>
                {shop.website ? (
                    <a href={shop.website} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-1.5 rounded-xl bg-[#f7f7f7] px-3 py-2 text-xs font-black text-[#414141]">
                        <Icon className="h-3.5 w-3.5"><circle cx="12" cy="12" r="10" /><path d="M2 12h20" /><path d="M12 2a15.3 15.3 0 0 1 0 20" /><path d="M12 2a15.3 15.3 0 0 0 0 20" /></Icon>
                        Website
                    </a>
                ) : (
                    <div className="flex items-center justify-center gap-1.5 rounded-xl bg-[#f7f7f7] px-3 py-2 text-xs font-black text-[#b3b3b3]">
                        <Icon className="h-3.5 w-3.5"><circle cx="12" cy="12" r="10" /><path d="M2 12h20" /><path d="M12 2a15.3 15.3 0 0 1 0 20" /><path d="M12 2a15.3 15.3 0 0 0 0 20" /></Icon>
                        Website
                    </div>
                )}
                <button
                    type="button"
                    onClick={handleShareReport}
                    className="flex items-center justify-center gap-1.5 rounded-xl bg-[#f7f7f7] px-3 py-2 text-xs font-black text-[#414141]"
                >
                    <Icon className="h-3.5 w-3.5"><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><path d="m8.6 13.5 6.8 4" /><path d="m15.4 6.5-6.8 4" /></Icon>
                    {shareCopied ? 'Copied' : 'Share'}
                </button>
            </div>
        </div>
    );
}

export default function PartnerShopDrawer({
    open,
    onClose,
    diagnosticTicketId,
    reportSnapshot = null,
    canRequestEstimate = Boolean(diagnosticTicketId),
    context = {},
    sentShopIds,
    onRequestCreated,
    showAdminLink = false,
    // When set (non-empty array of {key,title,dueLabel,status}) the drawer renders in
    // "maintenance" mode: maintenance-specific title, a Selected-maintenance list, and
    // "Maintenance Estimate / To be quoted" tiles instead of the diagnostic wording.
    maintenanceItems = null,
}) {
    const isMaintenance = Array.isArray(maintenanceItems) && maintenanceItems.length > 0;
    const [partnerShops, setPartnerShops] = useState([]);
    const [nearbyShops, setNearbyShops] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [loadWarning, setLoadWarning] = useState('');
    const [nearbyError, setNearbyError] = useState('');
    const [expandedNearbySearch, setExpandedNearbySearch] = useState(false);
    const [hasMoreNearbyShops, setHasMoreNearbyShops] = useState(false);
    const [referralLock, setReferralLock] = useState(null);
    const [shareConsent, setShareConsent] = useState(false);
    const [requestingShopId, setRequestingShopId] = useState('');
    const [localSentIds, setLocalSentIds] = useState(() => new Set());
    const initialZip = context.defaultZip || readLastZip() || DEFAULT_ZIP;
    const [zipInput, setZipInput] = useState(() => initialZip);
    const [searchedZip, setSearchedZip] = useState(() => initialZip);
    const [zipError, setZipError] = useState('');
    const [locationError, setLocationError] = useState('');
    // "Use my current location" (#697): asked for only on click and kept in memory only.
    // Once found, both partner and Google searches use it as their centre instead of the
    // ZIP. Updating the ZIP switches both searches back to that ZIP's centroid.
    const currentLocation = useCurrentLocation();
    const { clear: clearCurrentLocation } = currentLocation;
    const [activeCoords, setActiveCoords] = useState(null);
    const lastSearchCoordsRef = useRef(null);
    useEffect(() => {
        if (!open) {
            clearCurrentLocation();
            setActiveCoords(null);
            lastSearchCoordsRef.current = null;
        }
    }, [open, clearCurrentLocation]);
    const searchAbortRef = useRef(null);
    const profileRadiusMiles = Number(context.searchRadiusMiles) > 0 ? Number(context.searchRadiusMiles) : 50;

    const effectiveSentIds = useMemo(() => {
        const incoming = normalizeSentIds(sentShopIds);
        localSentIds.forEach((id) => incoming.add(String(id)));
        return incoming;
    }, [sentShopIds, localSentIds]);

    // Partner shops and Google results are fetched independently and settled separately:
    // one source failing must not blank out the other, because a driver with a dead car
    // would rather see half a list than an error.
    async function loadShops(zip, options = {}) {
        const includeOutsideRadius = Boolean(options.includeOutsideRadius);
        const coords = options.coords || null;
        searchAbortRef.current?.abort();
        const controller = new AbortController();
        searchAbortRef.current = controller;

        setIsLoading(true);
        setZipError('');
        setLocationError('');
        try {
            if (!coords && !isValidZip(zip)) throw new Error(ZIP_INVALID_MESSAGE);
            await validateShopSearchLocation({ location: zip, coords, signal: controller.signal });
        } catch (err) {
            if (controller.signal.aborted) return;
            setActiveCoords(lastSearchCoordsRef.current);
            if (coords) setLocationError(err.message);
            else setZipError(err.message);
            setIsLoading(false);
            return;
        }
        if (controller.signal.aborted) return;
        setActiveCoords(coords);
        if (!coords) clearCurrentLocation();
        setError('');
        setLoadWarning('');
        setNearbyError('');

        const [partnerResult, nearbyResult] = await Promise.allSettled([
            listPartnerShops({ zip, diagnosticTicketId, radiusMiles: profileRadiusMiles, coords }),
            searchNearbyShops({
                location: zip,
                issueSummary: context.symptomSummary || '',
                diagnosticTicketId,
                radiusMiles: profileRadiusMiles,
                includeOutsideRadius,
                coords,
                signal: controller.signal,
            }),
        ]);

        // A newer search started while this one was in flight — its results are the ones
        // that belong on screen, so drop these rather than overwrite them.
        if (controller.signal.aborted) return;
        // The search endpoint revalidates for API callers too. Preserve the last
        // successful search if that validation disagrees or is unavailable.
        if (nearbyResult.status === 'rejected' && [400, 503].includes(nearbyResult.reason?.status)) {
            setActiveCoords(lastSearchCoordsRef.current);
            if (coords) setLocationError(nearbyResult.reason.message);
            else setZipError(nearbyResult.reason.message);
            setIsLoading(false);
            return;
        }
        setSearchedZip(zip);
        lastSearchCoordsRef.current = coords;
        if (!coords) rememberLastZip(zip);
        setExpandedNearbySearch(includeOutsideRadius);

        let partners = partnerResult.status === 'fulfilled'
            ? partnerResult.value.filter((shop) => shop.shop_type !== 'nearby')
            : [];
        const nearby = nearbyResult.status === 'fulfilled' ? nearbyResult.value : [];
        const partnerLock = partnerResult.status === 'fulfilled' ? (partnerResult.value.referralLock || null) : null;
        const nearbyLock = nearbyResult.status === 'fulfilled' ? (nearbyResult.value.referralLock || null) : null;
        const lock = partnerLock || nearbyLock;
        if (lock) {
            const lockedShopId = String(lock.partner_shop_id || lock.shop?.partner_shop_id || '');
            partners = partners.filter((shop) => !lockedShopId || String(getShopId(shop)) === lockedShopId);
            if (!partners.length && lockedShopId) {
                partners = nearby.filter((shop) => String(getShopId(shop)) === lockedShopId);
            }
        }
        setReferralLock(lock);
        setShareConsent(Boolean(lock?.consented_at));
        if (partnerResult.status === 'rejected' && !lock) {
            setLoadWarning('Could not load CarFido partner shops. Showing nearby shops only.');
        }
        setPartnerShops(partners);

        // A partner that is also a Google listing would otherwise show up twice on one
        // screen — once as a partner and once as a plain nearby shop.
        const claimedPlaceIds = new Set(partners.map((shop) => shop.google_place_id).filter(Boolean));
        if (lock) {
            setNearbyShops([]);
            setNearbyError('');
            setHasMoreNearbyShops(false);
        } else if (nearbyResult.status === 'fulfilled') {
            setNearbyShops(nearby.filter((shop) => !claimedPlaceIds.has(shop.place_id)));
            setHasMoreNearbyShops(Boolean(nearby.hasMoreOutsideRadius));
        } else {
            setNearbyShops([]);
            setNearbyError('Could not load nearby shops from Google right now.');
            setHasMoreNearbyShops(false);
        }

        setIsLoading(false);
    }

    useEffect(() => {
        if (!open) return undefined;
        const nextZip = context.defaultZip || readLastZip() || DEFAULT_ZIP;
        setZipInput(nextZip);
        loadShops(nextZip);
        return () => searchAbortRef.current?.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, diagnosticTicketId, context.defaultZip, profileRadiusMiles]);

    // When a current location is found, search nearby shops around it.
    const foundPosition = currentLocation.status === 'found' ? currentLocation.position : null;
    useEffect(() => {
        if (!open || !foundPosition) return;
        loadShops(searchedZip, { coords: { latitude: foundPosition.latitude, longitude: foundPosition.longitude } });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [foundPosition]);

    // One search mode at a time: "location" while results come from the driver's position,
    // otherwise "zip". The ZIP the driver typed is kept across both.
    const searchMode = activeCoords ? 'location' : 'zip';
    const zipChanged = zipInput.trim() !== searchedZip;

    // Back to the ZIP search (same ZIP as before); a location answer still on its way is
    // ignored, and loadShops drops any in-flight location results.
    function handleSearchByZip() {
        clearCurrentLocation();
        setZipError('');
        loadShops(searchedZip);
    }

    const accuracyLabel = currentLocation.position?.accuracyMiles
        ? `Accuracy: within ${currentLocation.position.accuracyMiles.replace(/^less than /, '')}`
        : '';

    function handleUpdateZip() {
        if (!isValidZip(zipInput)) {
            setZipError(ZIP_INVALID_MESSAGE);
            return;
        }
        setZipError('');
        loadShops(zipInput);
    }

    function handleZipKeyDown(event) {
        if (event.key === 'Enter') {
            handleUpdateZip();
        }
    }

    async function handleRequestEstimate(shop) {
        const shopId = getShopId(shop);
        if (!diagnosticTicketId || !canRequestEstimate) {
            setError('Open a report-ready diagnostic before requesting a shop estimate.');
            return;
        }
        if (referralLock && String(shopId) !== String(referralLock.partner_shop_id)) {
            setError(`This referral job can only continue with ${referralLock.shop?.name || 'the referring shop'}.`);
            return;
        }
        if (referralLock && !referralLock.consented_at && !shareConsent) {
            setError(`Please consent before sharing diagnostic information with ${referralLock.shop?.name || 'the referring shop'}.`);
            return;
        }
        setRequestingShopId(String(shopId));
        setError('');
        try {
            if (referralLock && !referralLock.consented_at) {
                const consent = await consentShopReferralTicket(
                    diagnosticTicketId,
                    `Customer consented to share this diagnostic report with ${referralLock.shop?.name || 'the referring shop'}.`
                );
                setReferralLock(consent?.referral_lock || { ...referralLock, consented_at: new Date().toISOString() });
            }
            const request = await createPartnerRepairRequest({
                diagnostic_ticket_id: Number(diagnosticTicketId),
                partner_shop_id: Number(shopId),
                referral_consent_confirmed: Boolean(referralLock),
                notes: 'Customer requested an estimate from the CarFido diagnostic report.',
                report_snapshot: reportSnapshot || undefined,
            });
            setLocalSentIds((prev) => new Set([...prev, String(shopId)]));
            onRequestCreated?.(request);
        } catch (err) {
            setError(err?.message || 'Could not request estimate.');
        } finally {
            setRequestingShopId('');
        }
    }

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[90]">
            <button
                type="button"
                aria-label="Close shop drawer overlay"
                onClick={onClose}
                className="absolute inset-0 bg-black/45"
            />
            <aside className="absolute inset-x-0 bottom-0 mx-auto flex max-h-[88vh] max-w-2xl flex-col overflow-hidden rounded-t-[28px] border border-[#d9d9d9] bg-[#F9F9F9] shadow-2xl">
                <div className="flex justify-center pt-3">
                    <div className="h-1 w-9 rounded-full bg-[#d3d3d3]" />
                </div>

                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close shop drawer"
                    className="absolute right-4 top-4 z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-[#414141] shadow-md transition hover:text-[#414141]"
                >
                    <Icon><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></Icon>
                </button>

                <div className="flex-1 overflow-y-auto">
                <header className="border-b border-[#d9d9d9] bg-white px-5 py-4 pr-16">
                    <p className="text-[18px] font-black text-[#414141]">
                        {referralLock ? `Continue with ${referralLock.shop?.name || 'Your Shop'}` : 'Find a Shop for This Issue'}
                    </p>
                    {context.vehicleName ? <p className="mt-1 text-sm font-bold text-[#414141]">{context.vehicleName}</p> : null}
                    {context.symptomSummary ? <p className="mt-1 line-clamp-2 text-sm font-semibold text-[#414141]">{context.symptomSummary}</p> : null}
                </header>

                <div data-tooltip-boundary className="border-b border-[#d9d9d9] bg-white px-5 py-3" data-testid="shop-search-mode" data-mode={searchMode}>
                    {searchMode === 'location' ? (
                        <>
                            <p className="inline-flex min-w-0 items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.12em] text-[#087a43]">
                                <Icon className="h-3.5 w-3.5 shrink-0">
                                    <path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21z" /><circle cx="12" cy="9.5" r="2.5" />
                                </Icon>
                                Using current location
                            </p>
                            <p className="mt-1.5 text-[11px] font-semibold text-[#414141]">
                                {`Showing shops near your current location within ${profileRadiusMiles} miles.`}
                            </p>
                            {accuracyLabel ? (
                                <p role="status" className="mt-0.5 text-[11px] font-semibold text-[#414141]">{accuracyLabel}</p>
                            ) : null}
                            {/* Never disabled: switching back must work even while a slow location
                                search is loading (loadShops drops that search's results). */}
                            <button
                                type="button"
                                onClick={handleSearchByZip}
                                className="mt-3 flex h-10 w-full items-center justify-center gap-2 rounded-xl border border-[#d9d9d9] bg-white px-4 text-xs font-black text-[#414141] transition hover:bg-[#fafafa] disabled:cursor-wait disabled:opacity-70"
                            >
                                <Icon className="h-4 w-4 shrink-0">
                                    <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
                                </Icon>
                                Search by ZIP instead
                            </button>
                        </>
                    ) : (
                        <>
                            <div className="flex items-center gap-2">
                                <label htmlFor="shop-search-zip" className="text-[10px] font-black uppercase tracking-[0.12em] text-[#414141]">
                                    Search near ZIP code
                                </label>
                                {/* Same tooltip as the Settings page (hover/focus/tap, kept inside this box).
                                    Opens above the label so it never covers the ZIP field below. */}
                                <InfoTooltip
                                    label="About shop search"
                                    text={`Results use your Profile ZIP and ${profileRadiusMiles}-mile shop search radius. Update your Profile to change the default.`}
                                />
                            </div>
                            <div className="mt-2 flex items-center gap-2">
                                <div className="relative min-w-0 flex-1">
                                    <Icon className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#a4a4a4]">
                                        <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
                                    </Icon>
                                    <input
                                        id="shop-search-zip"
                                        type="text"
                                        inputMode="numeric"
                                        value={zipInput}
                                        onChange={(event) => {
                                            setZipInput(event.target.value);
                                            setZipError('');
                                        }}
                                        onKeyDown={handleZipKeyDown}
                                        placeholder="80212"
                                        className="h-10 w-full rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] pl-9 pr-3 text-sm font-black text-[#414141] outline-none transition focus:border-[#D81F26]"
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={handleUpdateZip}
                                    disabled={isLoading || !zipChanged}
                                    className={`h-10 shrink-0 rounded-xl px-3 text-xs font-black transition disabled:cursor-not-allowed ${
                                        zipChanged
                                            ? 'bg-[#D81F26] text-white hover:bg-[#B8181E] disabled:opacity-70'
                                            : 'bg-[#f5f5f5] text-[#414141]'
                                    }`}
                                >
                                    {isLoading ? 'Searching' : 'Update'}
                                </button>
                            </div>
                            <p className={`mt-1.5 text-[11px] font-semibold ${zipError ? 'text-[#b42318]' : 'text-[#414141]'}`}>
                                {zipError || `Showing shops near ZIP ${searchedZip} within ${profileRadiusMiles} miles.`}
                            </p>
                            <button
                                type="button"
                                onClick={currentLocation.request}
                                disabled={currentLocation.status === 'locating'}
                                className="mt-3 flex h-10 w-full items-center justify-center gap-2 rounded-xl border border-[#d9d9d9] bg-white px-4 text-xs font-black text-[#414141] transition hover:bg-[#fafafa] disabled:cursor-wait disabled:opacity-70"
                            >
                                <Icon className="h-4 w-4 shrink-0">
                                    <path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21z" /><circle cx="12" cy="9.5" r="2.5" />
                                </Icon>
                                {currentLocation.status === 'locating' ? 'Finding your location...' : 'Use my current location'}
                            </button>
                            {/* Denied / unavailable / timed out: say why, and the ZIP search stays usable. */}
                            {currentLocation.status === 'error' ? (
                                <p role="alert" className="mt-1 text-[11px] font-semibold leading-relaxed text-[#b42318]">{currentLocation.message}</p>
                            ) : null}
                        </>
                    )}
                </div>

                {locationError && <p role="alert" className="mx-5 mt-2 text-xs font-bold text-[#b42318]">{locationError}</p>}

                {/* Commented out per request — keep the code, just don't render it for now. */}
                {false && (
                <div className="mx-5 mt-4 rounded-2xl border border-[#bde7d5] bg-[#ecfff5] px-3.5 py-2.5">
                    <p className="text-[11px] font-bold leading-relaxed text-[#087a43]">
                        CarFido Partner Shops can receive the diagnostic report directly and send an itemized estimate back to a dedicated shop conversation. Nearby shops are contact-only.
                    </p>
                </div>
                )}

                {!canRequestEstimate && (
                    <div className="mx-5 mt-3 rounded-2xl border border-[#f7d58f] bg-[#fff8e8] px-4 py-3 text-sm font-bold text-[#936000]">
                        {isMaintenance
                            ? 'Preparing your maintenance request… you can review shop options now; "Request Estimate" turns on in a moment.'
                            : 'Generate or open a report-ready diagnostic before requesting an estimate. You can still review the shop options here.'}
                    </div>
                )}

                {referralLock && canRequestEstimate && (
                    <div className="mx-5 mt-3 rounded-2xl border border-[#d8d8d8] bg-[#f6f6f6] px-4 py-3">
                        <p className="text-sm font-black text-[#414141]">
                            This job was started from {referralLock.shop?.name || 'your referring shop'}.
                        </p>
                        <p className="mt-1 text-xs font-semibold leading-relaxed text-[#414141]">
                            Estimate and booking actions for this diagnostic are routed only to that shop. Future chats can still use any shop.
                        </p>
                        {!referralLock.consented_at && (
                            <label className="mt-3 flex items-start gap-2 text-xs font-bold leading-relaxed text-[#414141]">
                                <input
                                    type="checkbox"
                                    checked={shareConsent}
                                    onChange={(event) => setShareConsent(event.target.checked)}
                                    className="mt-0.5 h-4 w-4 rounded border-[#b9b9b9]"
                                />
                                I agree to share this diagnostic report and related issue details with {referralLock.shop?.name || 'the referring shop'}.
                            </label>
                        )}
                    </div>
                )}

                {loadWarning && (
                    <div className="mx-5 mt-3 rounded-2xl border border-[#d9d9d9] bg-white px-4 py-3 text-xs font-bold leading-relaxed text-[#414141]">
                        {loadWarning}
                    </div>
                )}

                {error && (
                    <div className="mx-5 mt-3 rounded-2xl border border-[#ffd2d2] bg-[#fff1f1] px-4 py-3 text-sm font-bold text-[#b42318]">
                        {error}
                    </div>
                )}

                <div className="px-5 py-4">
                    <p className="mb-3 text-[11px] font-semibold text-[#414141]">
                        {referralLock ? 'Referring shop for this job' : <>Repair shops near <span className="font-black text-[#414141]">{searchMode === 'location' ? 'your current location' : searchedZip}</span></>}
                    </p>
                    <section>
                        <div className="mb-3 flex items-center gap-2">
                            <Icon className="h-4 w-4 text-[#087a43]"><path d="M20 6 9 17l-5-5" /></Icon>
                            <p className="text-xs font-black uppercase tracking-[0.08em] text-[#414141]">
                                {referralLock ? 'Selected Shop' : 'CarFido Partner Shops'}
                            </p>
                            <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-black text-[#414141]">{partnerShops.length}</span>
                        </div>
                        {isLoading ? (
                            <div className="space-y-3">
                                {[1, 2].map((item) => (
                                    <div key={item} className="h-36 animate-pulse rounded-[22px] bg-white/80" />
                                ))}
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {partnerShops.map((shop) => {
                                    const shopId = String(getShopId(shop));
                                    return (
                                        <PartnerShopCard
                                            key={shopId}
                                            shop={shop}
                                            sent={effectiveSentIds.has(shopId)}
                                            requesting={requestingShopId === shopId}
                                            requestDisabledReason={canRequestEstimate ? '' : 'Report Required'}
                                            onRequest={handleRequestEstimate}
                                            showAdminLink={showAdminLink}
                                            isMaintenance={isMaintenance}
                                        />
                                    );
                                })}
                                {!partnerShops.length && (
                                    <div className="rounded-[22px] border border-[#d9d9d9] bg-white px-4 py-5 text-center text-sm font-bold text-[#414141]">
                                        {searchMode === 'location'
                                            ? 'No CarFido partner shops found near your current location.'
                                            : `No CarFido partners within ${profileRadiusMiles} miles of ${searchedZip} yet. Nearby shops are listed below.`}
                                    </div>
                                )}
                            </div>
                        )}
                    </section>

                    {!referralLock && <section className="mt-6">
                        <div className="mb-3 flex items-center gap-2">
                            <Icon className="h-4 w-4 text-[#414141]"><path d="M21 10c0 7-9 12-9 12S3 17 3 10a9 9 0 1 1 18 0z" /><circle cx="12" cy="10" r="3" /></Icon>
                            <p className="text-xs font-black uppercase tracking-[0.08em] text-[#414141]">Nearby Repair Shops</p>
                            <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-black text-[#414141]">{nearbyShops.length}</span>
                        </div>
                        {isLoading ? (
                            <div className="space-y-3">
                                {[1, 2].map((item) => (
                                    <div key={item} className="h-28 animate-pulse rounded-[22px] bg-white/80" />
                                ))}
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {nearbyShops.map((shop) => (
                                    <NearbyShopCard key={getShopId(shop)} shop={shop} />
                                ))}
                                {!nearbyShops.length && (
                                    <div className="rounded-[22px] border border-[#d9d9d9] bg-white px-4 py-5 text-center text-sm font-bold text-[#414141]">
                                        {nearbyError || `No repair shops found near ${searchMode === 'location' ? 'your current location' : searchedZip}.`}
                                    </div>
                                )}
                                {!expandedNearbySearch && hasMoreNearbyShops && (
                                    <button
                                        type="button"
                                        onClick={() => loadShops(searchedZip, { includeOutsideRadius: true, coords: activeCoords })}
                                        disabled={isLoading}
                                        className="flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-[#d9d9d9] bg-white px-4 text-sm font-black text-[#414141] transition hover:bg-[#fafafa] disabled:cursor-wait disabled:opacity-70"
                                    >
                                        <Icon className="h-4 w-4"><path d="M12 5v14" /><path d="M5 12h14" /></Icon>
                                        More shops
                                    </button>
                                )}
                            </div>
                        )}
                    </section>}
                </div>
                </div>
            </aside>
        </div>
    );
}
